import _ from "lodash";
import moment from "moment";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { getAccessLogsByPageTokenAsync } from "src/api/access/ac-api";
import { getContractApply } from "src/api/contract/contract-api";
import { ContractManageList } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import usePageTokenInfiniteQuery, { fetchAllFromPageToken, TokenPage } from "src/api/hooks/usePageTokenInfiniteQuery";
import { getVisitorsAsync } from "src/api/visitor/visitor-api";
import { ContractVisitorListModel } from "src/api/visitor/visitor-types";
import { BaseInputWithSearch, BaseSelect, BaseTable } from "src/components";
import RangeDatepicker from "src/components/RangeDatepicker";
import SearchResetButton from "src/components/SearchResetButton";
import useNavigate from "src/hooks/usePartnerNavigate";
import { useModal } from "src/recoil/modalState/hook";
import { qsParse } from "src/utils";
import { downloadExcelByColumns } from "src/utils/excelDownload";
import { AccessLogPageTokenParams, AccessLogSearchParams, AccessLogsResponse } from "../access-record-types";
import columns from "./columns/Columns";

const searchTypes = [
  { label: "전체", value: "" },
  { label: "장치명", value: "deviceName" },
  { label: "닉네임", value: "nickname" },
  { label: "이메일", value: "email" },
  { label: "회원번호", value: "memberNo" },
  { label: "방문자 ID", value: "contractVisitorId" },
];

// 이벤트(출입결과 그룹) 필터 - request query param: eventGroup
// 조건검색에서 이벤트 필터 비노출 처리 (추후 복구 가능하도록 주석 유지)
// const eventGroupOptions = [
//   { label: "전체", value: "" },
//   { label: "출입승인", value: "ACCESS_SUCCESS" },
//   { label: "출입실패", value: "ACCESS_FAILURE" },
//   { label: "출입거부", value: "ACCESS_REJECTED" },
// ];

// 인증 유형 필터 - request query param: accessType
const accessTypeOptions = [
  { label: "전체", value: "" },
  { label: "QR(Member)", value: "MEMBER_MUTABLE" },
  { label: "QR(Visitor)", value: "VISITOR_MUTABLE" },
  { label: "QR(Offline)", value: "MEMBER_AUTHENTICATION" },
  { label: "QR(Kakao)", value: "KAKAO_QR" },
  { label: "Face", value: "MEMBER_FIXED_FACE" },
  { label: "RF(Member)", value: "MEMBER_FIXED" },
  { label: "RF(Visitor)", value: "VISITOR_FIXED" },
  { label: "Mobile Card", value: "MEMBER_MOBILE" },
];

const PAGE_SIZE = 50;
const EXCEL_PAGE_SIZE = 400;
// 계약/방문자 id 를 한 번에 조회하는 최대 개수 (URL 길이 제한)
const LOOKUP_CHUNK_SIZE = 300;

const SEARCH_PARAM_KEYS: Array<keyof AccessLogSearchParams> = [
  "startDate",
  "endDate",
  "userType",
  "searchType",
  "searchValue",
  "accessType",
  "eventGroup",
];

// 기본 날짜 범위 생성 (7일 전 ~ 내일)
const getDefaultDateRange = (): [Date, Date] => {
  const sevenDaysAgo = moment().subtract(7, "day").toDate();
  const tomorrow = moment().add(1, "day").toDate();
  return [sevenDaysAgo, tomorrow];
};

// 날짜를 API 포맷으로 변환
const formatDateForApi = (date: Date, isEndDate = false): string => {
  return moment(date)[isEndDate ? "endOf" : "startOf"]("day").format("YYYY-MM-DDTHH:mm:ss.SSSZ");
};

// API 포맷 날짜를 Date 객체로 변환
const parseApiDate = (dateString: string): Date => {
  return moment(dateString).toDate();
};

const pickSearchParams = (parsed: Record<string, unknown>): AccessLogSearchParams => {
  const result: Record<string, string> = {};
  SEARCH_PARAM_KEYS.forEach((key) => {
    const value = parsed[key];
    if (typeof value === "string" && value !== "") {
      result[key] = value;
    }
  });
  return result as AccessLogSearchParams;
};

type ContractInfo = Partial<Pick<ContractManageList, "contractId" | "contractName" | "partnerId" | "spaceProductName">>;

// 계약명과 방문자 정보를 붙인 목록 행
type AccessLogContract = Omit<ContractInfo, "spaceProductName"> & { contractManageId: string; spaceProductName: string };
type AccessLogRow = Omit<AccessLogsResponse, "contractManageIds"> & {
  contractManageIds: AccessLogContract[];
  visitorInfo?: ContractVisitorListModel;
};

const AccessRecordList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setBaseModal } = useModal();
  const { partnerId } = useParams();
  // 첫 페이지만 전역 로딩바를 쓰고 다음 페이지는 목록 하단 상태 줄로 대신함
  const { executeAsync: getAccessLogsWithLoading } = useApiOperation(getAccessLogsByPageTokenAsync, { noErrorModal: true });
  const { executeAsync: getAccessLogsWithoutLoading } = useApiOperation(getAccessLogsByPageTokenAsync, {
    noErrorModal: true,
    noLoading: true,
  });
  const { executeAsync: getContractsWithLoading } = useApiOperation(getContractApply);
  const { executeAsync: getContractsWithoutLoading } = useApiOperation(getContractApply, { noLoading: true });
  const { executeAsync: getVisitorsWithLoading } = useApiOperation(getVisitorsAsync);
  const { executeAsync: getVisitorsWithoutLoading } = useApiOperation(getVisitorsAsync, { noLoading: true });

  // 적용된 검색 조건 (URL 기준)
  const searchParams = useMemo(() => pickSearchParams(qsParse(location.search)), [location.search]);

  // URL에서 날짜 범위를 초기화하는 함수
  const getInitialDateRange = useCallback((): [Date, Date] => {
    if (searchParams.startDate && searchParams.endDate) {
      try {
        return [parseApiDate(searchParams.startDate), parseApiDate(searchParams.endDate)];
      } catch (error) {}
    }
    return getDefaultDateRange();
  }, [searchParams.startDate, searchParams.endDate]);

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(getInitialDateRange);
  const [params, setParams] = useState<AccessLogSearchParams>(searchParams);

  useEffect(() => {
    setParams(searchParams);
  }, [searchParams]);

  // 전달받은 파라미터만으로 이동 (기존 params 와 병합하지 않음)
  const navigateWithExactParams = useCallback(
    (newQueryParams: AccessLogSearchParams, options?: { replace?: boolean }) => {
      const queryString = qs.stringify(newQueryParams, { allowDots: true });
      // 디코딩해서 넣으면 검색어의 %, &, # 가 URL에서 깨지므로 인코딩된 채로 이동
      const url = `${location.pathname}?${queryString}`;
      navigate(url, options);
    },
    [navigate, location.pathname],
  );

  const navigateWithQueryParams = useCallback(
    (passParams?: AccessLogSearchParams, options?: { replace?: boolean }) => {
      navigateWithExactParams({ ...params, ...(passParams || {}) }, options);
    },
    [navigateWithExactParams, params],
  );

  useEffect(() => {
    // URL에 날짜 파라미터가 있으면 해당 날짜로 설정 (새로고침 시 URL 유지)
    if (searchParams.startDate && searchParams.endDate) {
      setDateRange(getInitialDateRange());
    } else {
      // URL에 날짜 파라미터가 없으면 현재 날짜 기준으로 기본 날짜 설정 (메뉴 클릭 진입 / 검색해제 시)
      const defaultRange = getDefaultDateRange();
      setDateRange(defaultRange);

      const resetParams = {
        startDate: formatDateForApi(defaultRange[0]),
        endDate: formatDateForApi(defaultRange[1], true),
      };

      // 검색해제 시 이전 조건(이벤트/인증유형/검색어)이 되살아나지 않도록 병합 없이 초기화
      setParams(resetParams);
      navigateWithExactParams(resetParams, { replace: true });
    }
  }, [location.pathname, searchParams.startDate, searchParams.endDate]);

  const fetchContractsInChunks = useCallback(
    async (contractManageIds: string[], withLoading: boolean): Promise<ContractManageList[]> => {
      const getContracts = withLoading ? getContractsWithLoading : getContractsWithoutLoading;
      const responses = await Promise.all(
        _.chunk(contractManageIds, LOOKUP_CHUNK_SIZE).map((chunk) => getContracts({ contractManageIds: chunk.join(",") })),
      );
      return responses.flatMap((response) => response.data.data?.content ?? []);
    },
    [getContractsWithLoading, getContractsWithoutLoading],
  );

  const fetchVisitorsInChunks = useCallback(
    async (contractVisitorIds: string[], withLoading: boolean) => {
      const getVisitors = withLoading ? getVisitorsWithLoading : getVisitorsWithoutLoading;
      const responses = await Promise.all(
        _.chunk(contractVisitorIds, LOOKUP_CHUNK_SIZE).map((chunk) => getVisitors({ contractVisitorIds: chunk.join(",") })),
      );
      return responses.flatMap((response) => response.data.data?.contractVisitor ?? []);
    },
    [getVisitorsWithLoading, getVisitorsWithoutLoading],
  );

  const enrichAccessLogs = useCallback(
    async (logs: AccessLogsResponse[], { withLoading, strict }: { withLoading: boolean; strict: boolean }): Promise<AccessLogRow[]> => {
      const uniqueContractManageIds = [...new Set(logs.flatMap((log) => log.contractManageIds ?? []))];
      const uniqueVisitorIds = [
        ...new Set(
          logs
            .filter((log) => log.userType === "VISITOR")
            .map((log) => log.userId)
            .filter((id): id is string => id != null && id !== ""),
        ),
      ];

      const tolerate = <T,>(promise: Promise<T[]>): Promise<T[]> => (strict ? promise : promise.catch(() => []));

      const [contracts, visitors] = await Promise.all([
        uniqueContractManageIds.length > 0 ? tolerate(fetchContractsInChunks(uniqueContractManageIds, withLoading)) : Promise.resolve([]),
        uniqueVisitorIds.length > 0 ? tolerate(fetchVisitorsInChunks(uniqueVisitorIds, withLoading)) : Promise.resolve([]),
      ]);

      const contractMap = new Map<number, ContractInfo>(
        contracts
          .filter((contract) => Number(contract.partnerId) === Number(partnerId))
          .map((contract) => [
            Number(contract.contractManageId),
            {
              contractId: contract.contractId,
              contractName: contract.contractName,
              partnerId: contract.partnerId,
              spaceProductName: contract.spaceProductName,
            },
          ]),
      );

      return logs.map((log) => ({
        ...log,
        contractManageIds:
          log.contractManageIds
            ?.filter((id) => contractMap.has(Number(id)))
            .map((id) => {
              const contractInfo = contractMap.get(Number(id));
              return {
                contractId: contractInfo?.contractId,
                contractManageId: id,
                contractName: contractInfo?.contractName,
                partnerId: contractInfo?.partnerId,
                spaceProductName: contractInfo?.spaceProductName || "-",
              };
            }) ?? [],
        visitorInfo: log.userType === "VISITOR" ? visitors.find((visitor) => String(visitor.contractVisitorId) === String(log.userId)) : undefined,
      }));
    },
    [fetchContractsInChunks, fetchVisitorsInChunks, partnerId],
  );

  const fetchAccessLogPage = useCallback(
    async (
      pageToken: string | undefined,
      { pageSize, withLoading, strict }: { pageSize: number; withLoading: boolean; strict: boolean },
    ): Promise<TokenPage<AccessLogRow>> => {
      const { startDate, endDate } = searchParams;
      if (!startDate || !endDate) return { items: [] };

      const requestParams: AccessLogPageTokenParams = {
        ...searchParams,
        startDate,
        endDate,
        pageSize,
        ...(pageToken ? { pageToken } : {}),
      };
      const getAccessLogs = withLoading ? getAccessLogsWithLoading : getAccessLogsWithoutLoading;
      const { data: response } = await getAccessLogs(requestParams);
      const { logs, pagination } = response.data;

      return {
        items: await enrichAccessLogs(logs ?? [], { withLoading, strict }),
        nextPageToken: pagination?.nextPageToken ?? undefined,
        totalItemCount: pagination?.totalSize != null ? Number(pagination.totalSize) : undefined,
      };
    },
    [searchParams, getAccessLogsWithLoading, getAccessLogsWithoutLoading, enrichAccessLogs],
  );

  const fetchPage = useCallback(
    (pageToken?: string) => fetchAccessLogPage(pageToken, { pageSize: PAGE_SIZE, withLoading: !pageToken, strict: false }),
    [fetchAccessLogPage],
  );

  const { items, totalItemCount, nextPageToken, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage, retryLoadMore } =
    usePageTokenInfiniteQuery<AccessLogRow>({
      queryKey: ["accessLogList", partnerId, searchParams],
      fetchPage,
      enabled: Boolean(searchParams.startDate && searchParams.endDate),
    });

  const handleLoadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  // 날짜 범위 변경 핸들러
  const handleOnDateRangeChange = useCallback(
    (newDateRange: [Date | null, Date | null]) => {
      setDateRange(newDateRange);

      if (newDateRange[0] && newDateRange[1]) {
        navigateWithQueryParams({
          startDate: formatDateForApi(newDateRange[0]),
          endDate: formatDateForApi(newDateRange[1], true),
        });
      }
    },
    [navigateWithQueryParams],
  );

  const showDownloadResult = (success: boolean, error?: unknown) => {
    setBaseModal({
      isOpen: true,
      title: success ? "엑셀 다운로드가 완료되었습니다." : "엑셀 다운로드에 실패하였습니다.",
      ...(error ? { children: String(error) } : {}),
      btnRightTitle: "확인",
      onClick: () => setBaseModal({ isOpen: false }),
    });
  };

  // 엑셀용 columns 생성 (contractManageIds 대신 contractNames 사용)
  const excelColumns = useMemo(() => {
    return columns.map((col: { accessor?: string }) => {
      if (col.accessor === "contractManageIds") {
        return {
          ...col,
          accessor: "contractNames",
          Header: "계약명",
        };
      }
      return col;
    });
  }, []);

  const onDownloadExcel = async () => {
    if (isLoading || items.length === 0) {
      setBaseModal({ isOpen: true, title: "조회된 데이터가 없습니다.", btnRightTitle: "확인", onClick: () => setBaseModal({ isOpen: false }) });
      return;
    }

    try {
      const restItems = nextPageToken
        ? await fetchAllFromPageToken(
            (pageToken) => fetchAccessLogPage(pageToken, { pageSize: EXCEL_PAGE_SIZE, withLoading: true, strict: true }),
            nextPageToken,
          )
        : [];

      const excelData = [...items, ...restItems].map((item) => ({
        ...item,
        contractNames: item.contractManageIds.map((contract) => contract.contractName || contract.spaceProductName || "-").join(", "),
      }));

      const fileNamePrefix =
        dateRange[0] && dateRange[1]
          ? `출입기록조회-${moment(dateRange[0]).format("YYYYMMDD")}~${moment(dateRange[1]).format("YYYYMMDD")}`
          : `출입기록조회-전체`;

      await downloadExcelByColumns<AccessLogRow>({
        data: excelData,
        columns: excelColumns,
        fileNamePrefix,
        excludeAccessors: [],
        onNoData: () => {
          setBaseModal({ isOpen: true, title: "조회된 데이터가 없습니다.", btnRightTitle: "확인", onClick: () => setBaseModal({ isOpen: false }) });
        },
        onSuccess: () => {
          showDownloadResult(true);
        },
        onError: (error) => {
          console.log("error", error);
          showDownloadResult(false, error);
        },
      });
    } catch (error) {
      showDownloadResult(false, error);
    }
  };

  return (
    <div className="page-product-list">
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                <div className="minmax240">
                  <RangeDatepicker dateRange={dateRange} onChange={handleOnDateRangeChange} showClearButton={false} />
                </div>

                {/* 이벤트 필터 비노출 처리
                <div className="minmax160">
                  <BaseSelect
                    placeholder="이벤트"
                    value={params.eventGroup}
                    stateOptions={eventGroupOptions}
                    setStateValue={(eventGroup: AccessLogEventGroup | "") => {
                      setParams({ ...params, eventGroup });
                      navigateWithQueryParams({ ...params, eventGroup });
                    }}
                  />
                </div>
                */}

                <div className="minmax160">
                  <BaseSelect
                    placeholder="인증 유형"
                    value={params.accessType}
                    stateOptions={accessTypeOptions}
                    setStateValue={(accessType: string) => {
                      setParams({ ...params, accessType });
                      navigateWithQueryParams({ ...params, accessType });
                    }}
                  />
                </div>

                <BaseInputWithSearch
                  type="text"
                  selectValue={params.searchType}
                  inputValue={params?.searchValue || ""}
                  stateOptions={searchTypes}
                  setStateValue={(searchType: AccessLogSearchParams["searchType"]) => {
                    setParams({ ...params, searchType });
                  }}
                  onClearClick={() => navigateWithQueryParams({ ...params, searchValue: "" })}
                  onChange={(searchValue: string) => setParams({ ...params, searchValue })}
                  onKeyUp={() => navigateWithQueryParams()}
                  onSearchClick={() => navigateWithQueryParams()}
                />
                <SearchResetButton />
              </div>
            </section>
          </div>
        </div>

        <BaseTable
          key={JSON.stringify(searchParams)}
          data={items}
          columns={columns}
          className="no-fixed-height"
          totalElements={totalItemCount ?? 0}
          disabledSortHeaders={["datetime", "deviceName", "contractManageIds", "nickname", "email", "userId", "eventName", "userId2", "accessType"]}
          setOrders={() => {}}
          virtualized
          onLoadMore={handleLoadMore}
          hasMore={Boolean(hasNextPage)}
          isLoadingMore={isLoading || isFetchingNextPage}
          isLoadError={isError}
          onRetry={retryLoadMore}
          children={
            <button className="base-btn color-white icon-excel" onClick={onDownloadExcel}>
              엑셀받기
            </button>
          }
        />
      </div>
    </div>
  );
};

export default AccessRecordList;
